// src/components/Orders/OrderStatusModal.js
import React, { useState } from "react";
import axios from "axios";
import API_ROUTES from "../../config/apiRoutes";
import { useTranslation } from "../../contexts/LanguageContext";

const OrderStatusModal = ({ order, isOpen, onClose, onUpdate }) => {
  const { t } = useTranslation();
  const [orderStatus, setOrderStatus] = useState(order?.orderStatus || "");
  const [deliveryStatus, setDeliveryStatus] = useState(order?.deliveryStatus || "");
  const [remarks, setRemarks] = useState(order?.remarks || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent page reload

    try {
      setLoading(true);
      setError("");

      const token = localStorage.getItem("authToken");
      if (!token) {
        alert("Unauthorized! No auth token found.");
        return;
      }

      const response = await axios.patch(
        API_ROUTES.Orders.UPDATE(order._id),
        {
          orderStatus,
          deliveryStatus,
          remarks,
          customerAddress: order.customerAddress || {},
          customerMobile: order.customerMobile || "",
          customerEmail: order.customerEmail,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Order updated successfully:", response.data);

      if (typeof onUpdate === "function") {
        onUpdate(response.data);
      }

      onClose();
    } catch (err) {
      console.error("Error updating order:", err);
      if (err.response?.status === 401) {
        setError(t("orderModal.unauthorized"));
      } else {
        setError(t("orderModal.failedUpdate"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white p-6 rounded-lg shadow-lg w-96">
        <h2 className="text-lg font-bold mb-4">{t("orderModal.statusModalTitle")}</h2>
        {error && <p className="text-red-500 mb-2">{error}</p>}
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700 mb-1">{t("orderModal.orderStatus")}</label>
            <select
              className="w-full p-2 border rounded"
              value={orderStatus}
              onChange={(e) => setOrderStatus(e.target.value)}
            >
              <option value="">{t("orderModal.selectOrderStatus")}</option>
              <option value="pending">{t("orderModal.statusPending")}</option>
              <option value="processing">{t("orderModal.statusPaid")}</option>
              <option value="completed">{t("orderModal.statusCompleted")}</option>
              <option value="cancelled">{t("orderModal.statusCancelled")}</option>
              <option value="refunded">{t("orderModal.statusRefunded")}</option>
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 mb-1">{t("orderModal.deliveryStatus")}</label>
            <select
              className="w-full p-2 border rounded"
              value={deliveryStatus}
              onChange={(e) => setDeliveryStatus(e.target.value)}
            >
              <option value="">{t("orderModal.selectDeliveryStatus")}</option>
              <option value="created">{t("orderModal.deliveryCreated")}</option>
              <option value="picked_up">{t("orderModal.deliveryPickedUp")}</option>
              <option value="in_transit">{t("orderModal.deliveryInTransit")}</option>
              <option value="at_warehouse">{t("orderModal.deliveryAtWarehouse")}</option>
              <option value="out_for_delivery">{t("orderModal.deliveryOutForDelivery")}</option>
              <option value="delivered">{t("orderModal.deliveryDelivered")}</option>
              <option value="delivery_failed">{t("orderModal.deliveryFailed")}</option>
              <option value="returned">{t("orderModal.deliveryReturned")}</option>
              <option value="cancelled">{t("orderModal.deliveryCancelled")}</option>
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 mb-1">{t("orderModal.remarks")}</label>
            <textarea
              className="w-full p-2 border rounded"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder={t("orderModal.remarksPlaceholder")}
            />
          </div>

          <div className="flex justify-end space-x-2">
            <button
              type="button"
              className="px-4 py-2 bg-gray-500 text-white rounded"
              onClick={onClose}
            >
              {t("orderModal.cancel")}
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded"
              disabled={loading}
            >
              {loading ? t("orderModal.updating") : t("orderModal.confirm")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OrderStatusModal;
